"use client";

/**
 * PersonSelector Component
 * 
 * Uses the Standardized Drawer System for mobile person selection.
 * For comprehensive drawer usage guide, examples, and best practices, see:
 * 📖 apps/hub/docs/ui-components/STANDARDIZED_DRAWER_README.md
 */

import * as React from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../select";
import { Label } from "../label";
import { Button } from "../button";
import { Input } from "../input";
import { Users, User, ChevronDown, Search } from "lucide-react";
import { useUniversalAuth } from "../../lib/auth/OptimalAuthProvider";
import { useMediaQuery } from "../../hooks/use-media-query";
import { cn } from "../../lib/utils";
import { CentralizedDrawer, useGlobalDrawerStack } from "../global-drawer-stack";

interface PersonSelectorProps {
  selectedPerson: string;
  onPersonChange: (person: string) => void;
  disabled?: boolean;
  className?: string;
  hideLabel?: boolean;
}

const AVAILABLE_PEOPLE = [
  { value: "eliza.keffeler@westtn.consulting", label: "Eliza Keffeler" },
  { value: "charlie.keffeler@westtn.consulting", label: "Charlie Keffeler" },
  { value: "jimmytreb@gmail.com", label: "Jimmy Turner" },
];

const HIDDEN_PEOPLE = [
  { value: "miclainkeffeler@westtn.consulting", label: "Miclain Keffeler" },
];

export const PersonSelector = ({ 
  selectedPerson, 
  onPersonChange, 
  disabled = false,
  className,
  hideLabel = false
}: PersonSelectorProps) => {
  const { appUser } = useUniversalAuth();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { openDrawer, closeDrawer, isDrawerOpen } = useGlobalDrawerStack();
  const open = isDrawerOpen("mobile-person-selector");

  // Use a unique value for "My Calendar" to avoid key conflicts
  const myCalendarValue = "my-calendar";
  const userEmail = appUser?.email;
  
  // Filter out current user from available people to avoid duplicates
  // Also include hidden people if they are currently selected
  const otherPeople = React.useMemo(() => {
    const visiblePeople = AVAILABLE_PEOPLE.filter(person => person.value !== userEmail);
    const selectedHiddenPerson = HIDDEN_PEOPLE.find(person => person.value === selectedPerson);
    
    return selectedHiddenPerson ? [...visiblePeople, selectedHiddenPerson] : visiblePeople;
  }, [userEmail, selectedPerson]);
  
  // Create combined options list with "My Calendar" first, then others
  const allOptions = React.useMemo(() => [
    {
      value: myCalendarValue,
      label: "My Calendar",
      description: userEmail || "Your own calendar",
      icon: Users,
      isMyCalendar: true,
    },
    ...otherPeople.map(person => ({
      value: person.value,
      label: person.label,
      description: person.value,
      icon: User,
      isMyCalendar: false,
    }))
  ], [userEmail, otherPeople]);

  // Simple search state
  const [searchQuery, setSearchQuery] = React.useState("");
  
  // Filter options based on search query
  const filteredOptions = React.useMemo(() => {
    if (!searchQuery.trim()) return allOptions;
    return allOptions.filter(option => 
      option.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      option.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [allOptions, searchQuery]);

  // Find selected person info (check both my calendar and available people)
  // "My Calendar" is selected when selectedPerson matches the user's email
  const isMyCalendarSelected = selectedPerson === userEmail || selectedPerson === myCalendarValue;
  const selectedPersonInfo = isMyCalendarSelected
    ? { value: myCalendarValue, label: "My Calendar" }
    : AVAILABLE_PEOPLE.find(person => person.value === selectedPerson);

  const handleOptionSelect = (value: string) => {
    // If "My Calendar" is selected, use the user's email instead of myCalendarValue
    const actualValue = value === myCalendarValue ? (userEmail || value) : value;
    onPersonChange(actualValue);
    closeDrawer("mobile-person-selector");
    setSearchQuery(""); // Reset search when closing
  };

  const getDisplayValue = () => {
    if (isMyCalendarSelected) {
      return "My Calendar";
    }
    return selectedPersonInfo?.label || "Select person to view calendar";
  };

  // Mobile drawer implementation
  if (isMobile) {
    return (
      <div className={className}>
        {!hideLabel && <Label>Calendar</Label>}
        <Button
          variant="outline"
          onClick={() => openDrawer("mobile-person-selector")}
          disabled={disabled}
          className={cn(
            "w-full justify-between bg-card border-gray-300 focus:border-blue-500 focus:ring-blue-500 h-10",
            !hideLabel && "mt-1",
            disabled && "opacity-50 cursor-not-allowed"
          )}
        >
          <span className="truncate">{getDisplayValue()}</span>
          <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
        </Button>

        <CentralizedDrawer
          drawerId="mobile-person-selector"
          side="bottom"
          height="95vh"
          title="Select Calendar"
          description="Choose which calendar to display"
          showCloseButton={true}
          enableSwipeGesture={true}
        >
          <div className="flex-1 overflow-y-auto p-4">
          
          {/* Search Input */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search people..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Options List */}
          <div className="space-y-1">
            {filteredOptions.map((option: any, index: number) => {
              const IconComponent = option.icon;
              // Check if this option is selected
              // For "My Calendar", check if selectedPerson matches the user's email
              const isSelected = option.value === myCalendarValue
                ? isMyCalendarSelected
                : selectedPerson === option.value;
              
              return (
                <Button
                  key={option.value}
                  variant="ghost"
                  onClick={() => handleOptionSelect(option.value)}
                  className={cn(
                    "w-full justify-start p-4 h-auto space-x-3",
                    isSelected && "bg-blue-50 border border-blue-200"
                  )}
                >
                  <IconComponent className={cn(
                    "h-5 w-5 flex-shrink-0",
                    option.isMyCalendar ? "text-gray-600" : "text-blue-600"
                  )} />
                  <div className="flex-1 text-left min-w-0">
                    <div className="font-medium truncate">{option.label}</div>
                    <div className="text-sm text-gray-500 truncate">{option.description}</div>
                  </div>
                  {isSelected && (
                    <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0" />
                  )}
                </Button>
              );
            })}
          </div>
          </div>
        </CentralizedDrawer>

        {/* Status text */}
        {!hideLabel && selectedPersonInfo && !isMyCalendarSelected && (
          <p className="text-sm text-gray-500 mt-1">
            {selectedPersonInfo.label}
          </p>
        )}
      </div>
    );
  }

  // Desktop select implementation
  return (
    <div className={className}>
      {!hideLabel && <Label>Calendar</Label>}
      <Select 
        value={isMyCalendarSelected ? myCalendarValue : selectedPerson}
        onValueChange={(value: string) => {
          // If "My Calendar" is selected, use the user's email instead of myCalendarValue
          const actualValue = value === myCalendarValue ? (userEmail || value) : value;
          onPersonChange(actualValue);
        }}
        disabled={disabled}
      >
        <SelectTrigger className={cn(
          "bg-card border-gray-300 focus:border-blue-500 focus:ring-blue-500 h-10",
          !hideLabel && "mt-1"
        )}>
          <SelectValue placeholder="Select person to view calendar" />
        </SelectTrigger>
        <SelectContent className="bg-card border border-gray-200 shadow-lg z-[99999]">
          <SelectItem 
            value={myCalendarValue}
            className="hover:bg-card-50 focus:bg-card-50"
          >
            <div className="flex items-center space-x-2">
              <Users className="h-4 w-4 text-gray-600" />
              <div>
                <div className="font-medium">My Calendar</div>
                <div className="text-sm text-gray-500">
                  {userEmail || "Your own calendar"}
                </div>
              </div>
            </div>
          </SelectItem>
          {otherPeople.map((person) => (
            <SelectItem 
              key={person.value} 
              value={person.value}
              className="hover:bg-card-50 focus:bg-card-50"
            >
              <div className="flex items-center space-x-2">
                <User className="h-4 w-4 text-blue-600" />
                <div>
                  <div className="font-medium">{person.label}</div>
                  <div className="text-sm text-gray-500">{person.value}</div>
                </div>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {!hideLabel && selectedPersonInfo && !isMyCalendarSelected && (
        <p className="text-sm text-gray-500 mt-1">
          {selectedPersonInfo.label}
        </p>
      )}
    </div>
  );
};
